import { z } from 'zod';
import { QuoteStatusEnum } from './enums.types';

export enum SalesforceOrgTypeEnum {
  SANDBOX = 'SANDBOX',
  PRODUCTION = 'PRODUCTION',
  CUSTOM = 'CUSTOM',
}
export const SalesforceOrgTypeEnumZ = z.enum(SalesforceOrgTypeEnum);

export const SalesforceSchema = z.object({
  // refreshToken: z.string().nonempty(getRequiredMessage('Refresh Token')),
  // instanceUrl: z.string().nonempty(getRequiredMessage('Instance URL')),
  customUrl: z.string().nullish(),
  orgType: SalesforceOrgTypeEnumZ,
});

export type ISalesforceSchema = z.infer<typeof SalesforceSchema>;

export interface ISalesforce {
  instanceUrl: string;
  environment: SalesforceOrgTypeEnum;
  orgId: string;
  orgName: string;
  username: string;
}

// POST /connector/crm/salesforce/sync
export interface CrmResyncResponse {
  syncedEntities: CrmResyncResponseSuccess[];
  errors: CrmResyncResponseError[];
  syncSummary: 'success' | 'error' | 'partial_success';
}

export interface CrmResyncResponseSuccess {
  data: any;
  crmSyncData: {
    id: string;
    type: string;
  };
  type: string;
}

export interface CrmResyncResponseError {
  data: any;
  error: {
    operationKey: string;
    description: string;
  };
  type: string;
}

export interface CrmConnectionError {
  message: boolean;
}

export type DealAmountMappingValue =
  | 'AMOUNT'
  | 'ARR'
  | 'INCREMENTAL_ARR'
  | 'NONE';

export interface HubSpotSyncBehaviorToggle {
  enabled: boolean;
}

export interface HubSpotSyncBehavior {
  fromMn?: {
    dealLineItems?: HubSpotSyncBehaviorToggle;
    productCatalog?: HubSpotSyncBehaviorToggle;
  };
}

export interface HubSpotConnection {
  hubId: string;
  hubDomain: string;
  userEmail: string | null; // null when the connector couldn't look it up
  connectionError?: CrmConnectionError; // Not yet implemented on BE or in UI
  dealAmountMapping?: DealAmountMappingValue;
  syncBehavior?: HubSpotSyncBehavior;
}

export interface AttioConnection {
  workspaceId: string;
  workspaceName: string;
  userEmail: string | null; // null when the connector couldn't look it up
  connectionError?: CrmConnectionError; // Not yet implemented on BE or in UI
  quoteStatusToDealStageMappings: QuoteStatusToDealStageMappingType[];
  authorizedMemberId: string;
}

export interface CrmConnection {
  hubspot: HubSpotConnection | null;
  salesforce: ISalesforce | null; // FIXME: BE has not yet implemented this
  attio: AttioConnection | null;
}

export interface CrmHealthCheckError {
  error?: string;
  errorDescription?: string;
}

/**
 * The healthcheck responds 200 for a broken connection too; `error` is only
 * present when the connector could not authenticate with the CRM.
 */
export interface CrmHealthCheck {
  crmType: string;
  error?: CrmHealthCheckError;
}

export interface AttioDealStage {
  id: string;
  value: string;
}

export const QuoteStatusToDealStageMappingSchema = z.object({
  mnQuoteStatus: z.enum(QuoteStatusEnum),
  attioDealStageId: z.string(),
});

export type QuoteStatusToDealStageMappingType = z.infer<
  typeof QuoteStatusToDealStageMappingSchema
>;

export const QuoteStatusToDealStageMappingFormSchema = z.object({
  mappings: z.array(QuoteStatusToDealStageMappingSchema),
});

export type QuoteStatusToDealStageMappingForm = z.infer<
  typeof QuoteStatusToDealStageMappingFormSchema
>;

// This will be the eventual ISalesforce shape
//   {
//     "instanceUrl": "https://speed-drive-2653.scratch.my.salesforce.com",
//     "organizationId": "00D8N0000004mMYUAY",
//     "organizationType": "Enterprise Edition",
//     "organizationName": "ABC Corp",
//     "isSandbox": false,
//     "userId": "0055f000003EwifAAC",
//     "username": "austin@monetizenow.io.something",
//     "email": "austin@monetizenow.io",
//     "displayName": "Austin Turner",
//     "connectionError": null
//   }

export type CrmType = 'salesforce' | 'hubspot' | 'attio';

// GET /api/opportunities/{opportunityId}/hubspot/linkedAccounts
export interface HubspotAssociatedContact {
  hubSpotObjectId: string;
  monetizeContactId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  address?: string;
  state?: string;
  country?: string;
  zip?: string;
  city?: string;
  phone?: string;
}

export interface HubspotAssociatedCompany {
  hubSpotObjectId: string;
  monetizeAccountId?: string;
  name: string;
  billingAddress?: string;
  billingAddressLine2?: string;
  billingState?: string;
  billingCountry?: string;
  billingZip?: string;
  billingCity?: string;
  address?: string;
  addressLine2?: string;
  state?: string;
  country?: string;
  zip?: string;
  city?: string;
  associatedContacts: HubspotAssociatedContact[];
}

export interface HubspotLinkedAccountsResponse {
  dealId: string;
  monetizeOpportunityId: string;
  associatedCompanies: HubspotAssociatedCompany[];
}

// GET /connector/crm/salesforce/search/accounts/${accountId}/opportunities
export interface OpportunitySearchResponse {
  crm: CrmType;
  results: OpportunitySearchResponseItem[];
}

export interface OpportunitySearchResponseItem {
  closeDate: string;
  id: string;
  name: string;
  stage: string;
}

export const HubSpotInitQuoteRequestSchema = z.object({
  dealId: z.string(),
  dealName: z.string(),
});
export type HubSpotInitQuoteRequest = z.infer<
  typeof HubSpotInitQuoteRequestSchema
>;

export const HubSpotInitQuoteResponseSchema = z.object({
  accountId: z.string(),
  contactId: z.string(),
  opportunityId: z.string(),
  fieldMapping: z.record(z.string(), z.any()),
});
export type HubSpotInitQuoteResponse = z.infer<
  typeof HubSpotInitQuoteResponseSchema
>;
